/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { FormControl, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { selectBreakdownDetails } from 'modules/dashboard/selectors';
import { CheckboxDropdown } from 'modules/common/components';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';
import { getFocusOnFilter } from '../../../inner-filter/functions';
import { COMMON_FOCUS_ON_OPTIONS } from '../../../trend-tabs/constants';

/**
 * SearchFocusedOn
 *
 * Displays a checkbox dropdown for selecting "Focus On" options
 * relevant to the selected chart breakdown. Supports "Select All".
 *
 * @param {Object} props.filterData - Filter info for the selected scenario and visual.
 * @returns {JSX.Element} Checkbox dropdown for focus-on selection
 */
const SearchFocusedOn = ({ filterData, found }) => {
  const breakdownData = useSelector(selectBreakdownDetails);
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);
  //
  const [focusOnList, setFocusOnList] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  //
  const dispatch = useDispatch();
  useEffect(() => {
    // Set focus on list relevant for selected chart breakdown
    const focusOnFilter = getFocusOnFilter(breakdownData, found?.value?.breakdownName);
    setFocusOnList(focusOnFilter);
  }, [breakdownData, found]);
  // Determine if all focus-on options are selected
  useEffect(() => {
    if (focusOnList?.length > 0) {
      const allFocusOnOptionsSelected = focusOnList.length - 1 === found?.value?.values.length;
      setAllSelected(allFocusOnOptionsSelected);
    }
  }, [found?.value?.values, focusOnList]);
  // Handle checkbox selection change
  const handleChange = (event, value) => {
    let values = [];
    const isSelectAll = value.some(
      (option) => option.label === COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label
    );
    if (isSelectAll) {
      if (value?.length < focusOnList.length) {
        values = focusOnList
          .filter((e) => e.label !== COMMON_FOCUS_ON_OPTIONS.SELECT_ALL.label)
          ?.map((v) => v.label);
      }
    } else {
      values = value?.map((val) => val.label);
    }
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios?.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter === found) {
                acc[filterName] = {
                  ...filter,
                  value: {
                    ...filter.value,
                    values,
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (favoriteScenarios?.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter === found) {
                acc[filterName] = {
                  ...filter,
                  value: {
                    ...filter.value,
                    values,
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
    //
    if (searchScenarios?.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.entries(visual.filters).reduce((acc, [filterName, filter]) => {
              if (filter === found) {
                acc[filterName] = {
                  ...filter,
                  value: {
                    ...filter.value,
                    values,
                  },
                };
              } else {
                acc[filterName] = filter;
              }
              return acc;
            }, {}),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
  };
  //
  const selectedFocusOn = useMemo(
    () => focusOnList.filter((e) => found?.value?.values?.includes(e.label)),
    [found?.value?.values, focusOnList]
  );
  //
  return (
    <Grid
      container
      flexDirection="row"
      justifyContent="flex-end"
      alignItems="center"
      wrap="nowrap"
      bgcolor="#ffffff"
    >
      <FormControl>
        <CheckboxDropdown
          options={focusOnList}
          values={selectedFocusOn}
          label="Focus On"
          onchange={handleChange}
          isOptionEqualTo={(option, value) => option.id === value.id}
          id="search-focus-on"
          allSelected={allSelected}
          disabledOption={false}
        />
      </FormControl>
    </Grid>
  );
};
//
export default SearchFocusedOn;
